import React, { useEffect, useMemo, useState } from "react";
import { useSnapshot } from "valtio";
import { wizardStore } from "../../stores/wizard";
import Selector from "../commons/Selector";
import QuickViewLayout from "./QuickViewLayout";
import { Size } from "./Size";
import { Steps } from "../../constants";
import { Design } from "./Design";
import ColorManager from "./commonComps/ColorManager";
import IndicationManager from "./commonComps/3DEmblem/IndicationManager";
import LeatherIndicationManager from "./commonComps/EmblemLeather/LeatherIndicationManager";
import Scrolling from "./commonComps/Scrolling";
import ColorDetails from "./commonComps/ColorDetails";
import { Step } from "../../types/wizard";
import { TopProgressBar } from "../commons/PrograssBar";
import { getStepRuleConfig } from "../../config/productRules";
import { useApplyUIRulesEffects } from "../../hooks/useProductRules";

const stepsComponents: Record<string, React.ComponentType<any>> = {
  ProductSubtype: Selector,
  ProductType: Selector,
  Shape: Selector,
  FabricColor: Selector,
  FabricType: Selector,
  BackgroundPattern: Selector,
  BorderType: Selector,
  Backing: Selector,
  Layout: Selector,
  Combination: Selector,
  SewingChannel: Selector,
  Scrolling,
  SizeStandard: Size,
  HolographicUpload: Design,
  DesignType: Design,
  HolographicColors: ColorDetails,
  ThreadColors: ColorDetails,
  BorderColor: ColorManager,
  SewingGuideColor: ColorManager,
  EmblemLeather: LeatherIndicationManager,
  Emblem3d: IndicationManager,
  EmblemPrintStitch: IndicationManager
};

const layoutConfig = {
  default: { selectors: 1, uploads: 2, indications: 4, default: 3 },
  medium: { selectors: 1, uploads: 2, indications: 2, default: 1 },
  small: { selectors: 1, uploads: 1, indications: 1, default: 1 },
};

const stepGroups = {
  selectors: [
    Steps.ProductType, Steps.ProductSubtype, Steps.SizeStandard, Steps.FabricType,
    Steps.FabricColor, Steps.Backing, Steps.BorderType, Steps.Shape, Steps.Layout,
    Steps.Scrolling, Steps.BackgroundPattern, Steps.BorderColor, Steps.SewingGuideColor,
    Steps.Combination, Steps.SewingChannel
  ],
  uploads: [Steps.DesignType, Steps.HolographicUpload],
  indications: [Steps.Emblem3d, Steps.EmblemLeather, Steps.HolographicColors, Steps.EmblemPrintStitch],
};

const getScreenSize = () => {
  if (window.innerWidth <= 768) return "small";
  if (window.innerWidth <= 1024) return "medium";
  return "default";
};

const useScreenSize = () => {
  const [screenSize, setScreenSize] = useState<"default" | "medium" | "small">(getScreenSize());

  useEffect(() => {
    const handleResize = () => setScreenSize(getScreenSize());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return screenSize;
};

const getColumnPosition = (stepId: string, screenSize: "default" | "medium" | "small") => {
  if (stepGroups.selectors.includes(stepId)) return layoutConfig[screenSize].selectors;
  if (stepGroups.uploads.includes(stepId)) return layoutConfig[screenSize].uploads;
  if (stepGroups.indications.includes(stepId)) return layoutConfig[screenSize].indications;
  return layoutConfig[screenSize].default;
};

const QuickViewWizard: React.FC = () => {
  const wizSnap = useSnapshot(wizardStore);
  const screenSize = useScreenSize();
  useApplyUIRulesEffects();
  console.log(wizardStore)

  useEffect(() => {
    const currentUrl = window.location.href;
    const newUrl = currentUrl.replace(/(#.*)$/, '#');
    window.history.replaceState({}, '', newUrl);
  }, []);

  // Get product type/subtype for rule checking
  const productType = useMemo(() => {
    const data = wizSnap.emblemData[Steps.ProductType];
    return data ? { code: data.code, name: data.name } : null;
  }, [wizSnap.emblemData[Steps.ProductType]]);

  const productSubtype = useMemo(() => {
    const data = wizSnap.emblemData[Steps.ProductSubtype];
    return data ? { code: data.code, name: data.name } : null;
  }, [wizSnap.emblemData[Steps.ProductSubtype]]);

  const columns = useMemo(() => {
    return wizSnap.steps
      .filter((step) => step.id !== Steps.SizeStandard)
      .filter((step) => {
        // Check if step should be hidden by product rules
        const stepRule = getStepRuleConfig(step.id, productType, productSubtype);
        return !stepRule?.hidden;
      })
      .map((step) => {
        const StepComponent = stepsComponents[step.id];
        const columnNumber = getColumnPosition(step.id, screenSize);
        return StepComponent
          ? {
            column: columnNumber,
            step,
            content: (
              <StepComponent
                key={step.id}
                step={step}
                stepId={step?.id}
                column={columnNumber}
              />
            ),
          }
          : null;
      })
      .filter(Boolean);
  }, [wizSnap.steps, screenSize]);

  return (
    <>
      <TopProgressBar />
      <div className="flex h-full py-2">

        <div className="w-full">
          <QuickViewLayout
            columns={columns as { column: number; step: Step; content: JSX.Element }[]}
            screenSize={screenSize} />
        </div>
      </div>
    </>
  );
};

export default QuickViewWizard;